// Billing runs (billing_runs_list) and the charges they started (payments_list, source billing_run or retry).

export interface BillingRun {
  id: number;
  trigger: string;
  run_date: string;
  started_at: string;
  finished_at: string | null;
  status: string;
  invoices_created: number;
  charges_queued: number;
  approvals_created: number;
  summary: { message?: string; reason?: string; subscriptions_ended?: number; installments_invoiced?: number } | null;
  error: string | null;
  started_by: string;
}

export interface RunPayment {
  id: number;
  status: string;
  amount_cents: number;
  attempted_at: string;
  source: string | null;
}

export interface RunCounts {
  charged: number;
  chargedCents: number;
  failed: number;
  failedCents: number;
  processing: number;
}

/** Payments a billing run starts: renewals and installments (billing_run) and retries of earlier declines (retry). */
export const isRunPayment = (p: RunPayment) => p.source === "billing_run" || p.source === "retry";

/**
 * Counts each run's charges. Runs queue their charges and the worker makes them within minutes, so a payment
 * belongs to the latest run that started before it (runs come newest first, as billing_runs_list returns them).
 */
export function countRunPayments(runs: BillingRun[], payments: RunPayment[]): Map<number, RunCounts> {
  const out = new Map<number, RunCounts>();
  const byStart = [...runs].sort((a, b) => b.started_at.localeCompare(a.started_at));
  for (const r of byStart) out.set(r.id, { charged: 0, chargedCents: 0, failed: 0, failedCents: 0, processing: 0 });
  for (const p of payments) {
    if (!isRunPayment(p)) continue;
    const t = new Date(p.attempted_at).getTime();
    const run = byStart.find((r) => new Date(r.started_at).getTime() <= t);
    if (!run) continue;
    const c = out.get(run.id)!;
    if (p.status === "succeeded") {
      c.charged++;
      c.chargedCents += p.amount_cents;
    } else if (p.status === "failed") {
      c.failed++;
      c.failedCents += p.amount_cents;
    } else if (p.status === "pending" || p.status === "unknown") c.processing++;
  }
  return out;
}

/** The run's result in plain words. */
export function runResult(r: BillingRun): { label: string; tone: "success" | "warning" | "destructive" | "muted" } {
  if (r.status === "skipped") return { label: "skipped: runs are off", tone: "muted" };
  if (r.status === "failed") return { label: "failed", tone: "destructive" };
  if (r.status === "running" || !r.finished_at) return { label: "running", tone: "warning" };
  return { label: "ran", tone: "success" };
}

export const TRIGGER: Record<string, string> = { schedule: "scheduled", manual: "run by hand", test: "test" };
